import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import z from 'zod'
import { useImpossibleReasons } from '../../../hooks/useImpossibleReasons'
import { useInvalidateQueries } from '../../../hooks/useInvalidateQueries'
import {
  getUploadServicePhotoSettings,
  improvementRequired,
  serviceImpossible,
} from '../../../services/ApiService'
import { CustomFileType } from '../../SingleRequestPage/components/SingleRequestDrawer/content/DrawerContentInstallation'
import {
  getDeleteFileFn,
  getHandleSetFiles,
  getSettingField,
  onSuccessRework,
} from '../helpers/rework-drawer.helpers'
import { useSingleServiceRequest } from './useSingleServiceRequest'

const impossibleReasonForm = z.object({
  impossibleReasonId: z.string().min(1),
  impossibleReasonCustomDescription: z.string(),
})

type ImpossibleReasonFormType = z.infer<typeof impossibleReasonForm>
const defaultValues: ImpossibleReasonFormType = {
  impossibleReasonId: '',
  impossibleReasonCustomDescription: '',
}

const resolver = zodResolver(impossibleReasonForm)

export const useToReworkDrawer = (
  mode: 'to-rework' | 'to-impossible',
  id?: number,
  dropErrorFromThisPage?: boolean,
) => {
  const invalidate = useInvalidateQueries()
  const intl = useIntl()
  const {
    control,
    watch,
    formState: { isValid },
    reset,
  } = useForm<ImpossibleReasonFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  })
  const { data: impossibleReasons } = useImpossibleReasons()
  const { data: request } = useSingleServiceRequest(dropErrorFromThisPage)
  const [files, setFiles] = useState<CustomFileType[] | null>(null)
  const [comment, setComment] = useState('')
  const settingsField = getSettingField(mode)

  const { data } = useQuery({
    queryKey: ['get-photo-setings'],
    queryFn: () => getUploadServicePhotoSettings(),
  })

  const getParams = () => ({
    orderId: request!.id,
    comment,
    files: files?.map((file) => file.file),
  })

  const reason = impossibleReasons?.find(
    (r: any) => r.name === watch('impossibleReasonId'),
  )

  const onReset = () => reset(defaultValues)

  const { mutateAsync: markAsImpossible, isPending } = useMutation({
    mutationKey: ['mark-service-as-impossible'],
    mutationFn: (otherFiles?: CustomFileType[] | null) =>
      serviceImpossible({
        orderId: id || request?.id!,
        files: (otherFiles ?? files)?.map((file) => file.file),
        impossibleReasonId: reason?.id,
        impossibleReasonCustomDescription: reason?.isCustomOption
          ? watch('impossibleReasonCustomDescription')
          : undefined,
      }),
    onSuccess: (res) => onSuccessRework(res, invalidate, onReset),
  })

  const { mutateAsync: sendToRework, isPending: isSending } = useMutation({
    mutationKey: ['send-service-to-rework'],
    mutationFn: () => improvementRequired(getParams()),
    onSuccess: (res) => onSuccessRework(res, invalidate, onReset),
  })

  const photoSettings = data?.find((item: any) => item.key === settingsField)
  const handleSetFiles = getHandleSetFiles(intl, photoSettings, files, setFiles)
  const deleteFile = getDeleteFileFn(files, setFiles)

  const onSubmit = async (otherFiles?: CustomFileType[] | null) => {
    if (mode === 'to-impossible') {
      const res = await markAsImpossible(otherFiles)
      return res
    } else {
      await sendToRework()
    }
  }

  const showExplanation = reason?.isCustomOption
  const impossibleError = mode === 'to-impossible' && !isValid
  const formError =
    showExplanation && !watch('impossibleReasonCustomDescription').trim()

  const disabled =
    (mode === 'to-rework' && !comment.trim()) || impossibleError || formError

  const drawerName =
    mode === 'to-impossible'
      ? 'Обслуживание не выполнено'
      : 'Вернуть на доработку'

  const photosTitle = `Загрузите от ${photoSettings?.minLimitPerLoad} до ${photoSettings?.maxLimitPerLoad} фотографий`

  return useMemo(
    () => ({
      drawerName,
      photosTitle,
      files,
      deleteFile,
      comment,
      setComment,
      handleSetFiles,
      intl,
      onSubmit,
      isLoading: isPending || isSending,
      disabled,
      showExplanation,
      watch,
      isValid,
      control,
      impossibleReasons,
    }),
    [
      drawerName,
      photosTitle,
      files,
      deleteFile,
      handleSetFiles,
      isPending,
      isSending,
      comment,
      disabled,
      showExplanation,
      impossibleReasons,
    ],
  )
}
